import { useEffect, useState, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Dialog,
  Button,
  Field,
  Divider,
  Select,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { HierarchyLevelType } from 'src/enums/hierarchy';
import { createHierarchyItem, updateHierarchyItem } from 'store/entities/hierarchy/hierarchy';
import { Input } from 'client/common/form-inputs';
import translations from 'src/internationalisation/translation-map.json';
import { getResolver } from 'src/validation/resolver';
import { projectEstimatesSchemaValidator } from 'schemas/ajv-validators';
import { generatePath, navigateToPath } from 'store/navigation/navigation';
import { routes } from 'routes/routes';
import { IDesign, IEstimate } from 'client/common/interfaces/hierarchy.interfaces.ts';
import { FeatureFlagName } from 'src/enums/feature-flags';
import type { TRootState } from 'src/store/store-types';

type EstimateModalProps = PropsFromRedux & {
  companyId: string;
  activeDesign: IDesign | null;
  estimates: IEstimate[];
  isAdding: boolean;
  editItem: IEstimate | null;
  setEditItem: (data: IEstimate | null) => void;
  itemModalVisibleUpdated: (data: { type: HierarchyLevelType; value: boolean }) => void;
  currencyTemplate: ICurrencyTemplate[];
};

interface ICurrencyTemplate {
  currencyTemplateId: string;
  name: string;
  primaryCurrency: string;
  additionalCurrencies: any[];
  isDefault?: boolean;
}

const EstimateModal = ({
  companyId,
  itemModalVisibleUpdated,
  createHierarchyItem,
  updateHierarchyItem,
  activeDesign,
  editItem,
  setEditItem,
  isAdding,
  estimates,
  navigateToPath,
  currencyTemplate,
}: EstimateModalProps) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      design: activeDesign?.name,
      designid: activeDesign?.designid,
      name: editItem?.name || '',
      estimateid: editItem?.estimateid || null,
    },
    resolver: getResolver(projectEstimatesSchemaValidator),
  });

  const [projectCurrencySettings, setProjectCurrencySettings] = useState<any>(
    currencyTemplate?.find((item: any) => item.isDefault) || {},
  );
  const [selectedCurrencyTemplateId, setSelectedCurrencyTemplateId] = useState(
    currencyTemplate?.find((item: any) => item.isDefault)?.currencyTemplateId || null,
  );
  const currencyTemplateOptions = currencyTemplate?.map((item: any) => ({
    label: item.name,
    value: item.currencyTemplateId,
  }));

  const isProjectCurrencySettingsActive = FeatureFlags.isActive(FeatureFlagName.UserSetCurrency);

  const initialActiveEstimateId = useRef(estimates.find((item) => item.active)?.estimateid ?? null);

  useEffect(() => {
    const estimate = estimates.find((item) => item.active);
    if (estimate && estimate.estimateid !== initialActiveEstimateId.current) {
      navigateToPath(generatePath(routes.overview, { company: companyId, project: estimate.estimateid }, true));
      itemModalVisibleUpdated({ type: HierarchyLevelType.Estimate, value: false });
    }
  }, [estimates]);

  const onSubmit = handleSubmit(async (data) => {
    delete data.design;
    if (editItem) {
      updateHierarchyItem(companyId, HierarchyLevelType.Estimate, data.estimateid, {
        ...editItem,
        ...data,
      });
    } else if (isProjectCurrencySettingsActive) {
      createHierarchyItem(companyId, HierarchyLevelType.Estimate, {
        name: data.name,
        designid: activeDesign?.designid,
        datasetId: activeDesign?.datasetid || null,
        projectCurrencySettings,
      });
    } else {
      createHierarchyItem(companyId, HierarchyLevelType.Estimate, {
        name: data.name,
        designid: activeDesign?.designid,
        datasetId: activeDesign?.datasetid || null,
      });
    }

    setEditItem(null);
  });
  const onClose = () => {
    setEditItem(null);
    itemModalVisibleUpdated({
      type: HierarchyLevelType.Estimate,
      value: false,
    });
  };

  useEffect(() => {
    if (selectedCurrencyTemplateId) {
      const selectedTemplate: any = currencyTemplate?.find(
        (item: any) => item.currencyTemplateId === selectedCurrencyTemplateId,
      );
      setProjectCurrencySettings({
        primaryCurrency: selectedTemplate?.primaryCurrency || '',
        additionalCurrencies: selectedTemplate?.additionalCurrencies || [],
      });
    }
  }, [selectedCurrencyTemplateId]);

  return (
    <Modal visible centered>
      <Dialog
        dialog={{
          heading: editItem ? t(translations.projects_editEstimate) : t(translations.projects_addNewEstimate),
          width: '600px',
          content: (
            <form>
              <Field label={t(translations.design)} labelLeft labelWidth="80px">
                <Input name="design" control={control} errors={errors} disabled={true} />
              </Field>
              <Field label={t(translations.estimate)} labelLeft labelWidth="80px">
                <Input name="name" control={control} errors={errors} disabled={isAdding} />
              </Field>
              {isProjectCurrencySettingsActive && !editItem && (
                <>
                  <Divider />
                  <Field label={t(translations.currency_template)} labelLeft labelWidth={80}>
                    <Select
                      name={'currencyTemplate'}
                      options={currencyTemplateOptions}
                      value={selectedCurrencyTemplateId}
                      onChange={(e) => setSelectedCurrencyTemplateId(e.target.value)}
                    />
                  </Field>
                </>
              )}
            </form>
          ),
          footer: (
            <>
              <Button label={t(translations.done)} colored onClick={onSubmit} loading={isAdding} disabled={isAdding} />
              <Button label={t(translations.cancel)} onClick={onClose} disabled={isAdding} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapStateToProps = ({ entities }: TRootState) => {
  const { currencyTemplate } = entities.companySettings;
  return { currencyTemplate: currencyTemplate as ICurrencyTemplate[] };
};

const mapDispatchToProps = {
  createHierarchyItem,
  updateHierarchyItem,
  navigateToPath,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(EstimateModal), {
  style: { position: 'absolute', bottom: 0, zIndex: 1000 },
});

export { Container as EstimateModal };
